import { apiUrl } from "../utils/api";

/**
 * Die dauerhafte Mail-Ablage (Cluster ABLAGE, M1–M3 / E09, E11).
 *
 * Jede archivierte oder einem Vorgang zugeordnete Mail wird server-seitig
 * zusätzlich als vollständige `.eml` gesichert und als KMVS-Dokument geführt.
 * Dieser Service ist das FE-Gegenstück zu `/api/mail/archive` — Spiegel von
 * `MailArchiveDto` (apps/api/src/mail/mail-archive.dto.ts).
 */

export type MailArchiveStatus = "pending" | "stored" | "failed" | "skipped" | "purged";

/** Knappe Ablage-Rückmeldung der Archivier-/Verschiebe-Endpunkte. */
export interface MailArchiveStatusInfo {
    id: number;
    status: MailArchiveStatus;
    documentId: number | null;
    /** Klartextgrund bei `failed` und `skipped` (E11: „zu groß"). */
    error: string | null;
}

export interface MailArchiveItem {
    id: number;
    mailbox: string;
    messageId: string;
    conversationId: string | null;
    subject: string | null;
    senderName: string | null;
    senderEmail: string | null;
    recipients: string | null;
    receivedAt: string | null;
    status: MailArchiveStatus;
    trigger: string;
    attempts: number;
    lastError: string | null;
    /** Id des abgelegten `.eml`-Dokuments — Sprung in den vorhandenen Viewer. */
    documentId: number | null;
    fileName: string | null;
    sizeBytes: number | null;
    attachmentsImported: number;
    contact: { id: number; displayName: string } | null;
    vorgang: { id: number; titel: string | null } | null;
    storedAt: string | null;
    createdAt: string | null;
}

export interface MailArchiveListParams {
    mailbox?: string;
    status?: Exclude<MailArchiveStatus, "purged">;
    assigned?: "true" | "false";
    contactId?: number;
    vorgangId?: number;
    q?: string;
    limit?: number;
    offset?: number;
}

export interface MailArchiveStats {
    total: number;
    byStatus: Record<string, number>;
    bytesTotal: number;
    last1d: number;
    last7d: number;
    last30d: number;
    dedupedTotal: number;
    skippedTotal: number;
    avgSizeBytes: number;
    p95SizeBytes: number;
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
    const res = await fetch(url, init);
    if (!res.ok) {
        let detail = "";
        try {
            const body = await res.json();
            detail = typeof body?.message === "string" ? body.message : "";
        } catch {
            /* Antwort ohne JSON-Körper — der Status genügt. */
        }
        throw new Error(detail || `Ablage-Aufruf fehlgeschlagen: ${res.status}`);
    }
    return res.json() as Promise<T>;
}

export const mailArchiveService = {
    async list(params: MailArchiveListParams = {}): Promise<{ items: MailArchiveItem[]; total: number }> {
        const query: Record<string, string | number> = {};
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== null && v !== "") query[k] = v as string | number;
        }
        return fetchJson(apiUrl("mail/archive", query));
    },

    async get(id: number): Promise<MailArchiveItem> {
        return fetchJson(apiUrl(`mail/archive/${id}`));
    },

    /** Kontakt/Vorgang nachtragen (M3-Auflösung) — 403 bei unsichtbarem Kontakt. */
    async assign(
        id: number,
        body: { contactId?: number | null; vorgangId?: number | null },
    ): Promise<{ ok: true; archive: MailArchiveItem }> {
        return fetchJson(apiUrl(`mail/archive/${id}/assign`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    },

    /**
     * Erneut versuchen — auch für `skipped` (E11). `maxBytesOverride` ist
     * serverseitig admin/gfgl vorbehalten und gedeckelt.
     */
    async retry(id: number, opts: { maxBytesOverride?: number } = {}): Promise<{ ok: true; archive: MailArchiveItem }> {
        return fetchJson(apiUrl(`mail/archive/${id}/retry`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(opts),
        });
    },

    /** Soft-Delete (admin/gfgl): hängt das Dokument zusätzlich vom Kontakt ab. */
    async softDelete(id: number): Promise<{ ok: true }> {
        return fetchJson(apiUrl(`mail/archive/${id}`), { method: "DELETE" });
    },

    async stats(mailbox?: string): Promise<MailArchiveStats> {
        return fetchJson(apiUrl("mail/archive/stats", mailbox ? { mailbox } : undefined));
    },
};
